import { useState, type ReactNode } from "react";
import { Code2, Cpu } from "lucide-react";
import { cn } from "@/lib/utils";
import { STAGES, type Stage } from "./labels";

/**
 * Small building blocks shared by the new-look screens
 * (docs/new-look/PLAN.md, step 0.4). Light (blue) and dark (orange) themes.
 */

/** One color per stage; text, background and dot. Each text/background pair passes 4.5:1. */
const STAGE_TONE: Record<Stage, { chip: string; dot: string; bar: string }> = {
  idea: {
    chip: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-200",
    dot: "bg-violet-600 dark:bg-violet-400",
    bar: "bg-violet-600 dark:bg-violet-400",
  },
  blueprint: {
    chip: "bg-cyan-100 text-cyan-800 dark:bg-cyan-500/15 dark:text-cyan-200",
    dot: "bg-cyan-600 dark:bg-cyan-400",
    bar: "bg-cyan-600 dark:bg-cyan-400",
  },
  build: {
    chip: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
    dot: "bg-blue-600 dark:bg-blue-400",
    bar: "bg-blue-600 dark:bg-blue-400",
  },
  done: {
    chip: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
    dot: "bg-green-600 dark:bg-green-400",
    bar: "bg-green-600 dark:bg-green-400",
  },
};

export function StageChip({
  stage,
  children,
  className,
}: {
  stage: Stage;
  children?: ReactNode;
  className?: string;
}) {
  const tone = STAGE_TONE[stage];
  return (
    <span
      data-stage={stage}
      className={cn(
        "inline-flex min-h-6 items-center gap-1.5 self-start whitespace-nowrap rounded-full px-2.5 text-xs font-semibold",
        tone.chip,
        className
      )}
    >
      <span aria-hidden="true" className={cn("h-1.5 w-1.5 rounded-full", tone.dot)} />
      {children ?? STAGES[stage]}
    </span>
  );
}

export type Kind = "software" | "device";

export function KindBadge({ kind, className }: { kind: Kind; className?: string }) {
  const device = kind === "device";
  const Icon = device ? Cpu : Code2;
  return (
    <span
      role="img"
      aria-label={device ? "Device" : "Software"}
      title={device ? "Device" : "Software"}
      className={cn(
        "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
        device
          ? "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200"
          : "bg-blue-50 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
        className
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </span>
  );
}

export function ProgressBar({
  value,
  label,
  stage = "build",
  className,
}: {
  /** 0–100; values outside are clamped. */
  value: number;
  /** What the bar measures, for screen readers, e.g. "Build progress". */
  label: string;
  stage?: Stage;
  className?: string;
}) {
  const pct = Math.round(Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0)));
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-muted", className)}
    >
      <div
        className={cn("h-full rounded-full transition-[width]", STAGE_TONE[stage].bar)}
        style={{ width: `${String(pct)}%` }}
      />
    </div>
  );
}

export function SectionCard({
  title,
  action,
  children,
  className,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-label={title}
      className={cn("min-w-0 rounded-2xl border border-border bg-background p-4 sm:p-5", className)}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border px-6 py-10 text-center">
      <p className="text-base font-medium text-foreground">{title}</p>
      {body && <p className="max-w-md text-sm text-muted-foreground">{body}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * Wraps a "send to my machine" trigger (today's dialog button) so it looks
 * like the new look's main action: full width, primary, rounded.
 */
export const RUN_BUTTON =
  "[&>button]:min-h-11 [&>button]:w-full [&>button]:rounded-xl [&>button]:bg-primary [&>button]:font-semibold [&>button]:text-primary-foreground [&>button:hover]:bg-primary/90";

/**
 * Long text shown short at first (about eight lines), with "Show more" to
 * read it all. The button only appears when the text is long.
 */
export function ShowMore({
  children,
  long,
  className,
}: {
  children: ReactNode;
  /** Whether the content is long enough to fold (e.g. text over ~500 characters). */
  long: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const folded = long && !open;
  return (
    <div className={className}>
      <div
        className={cn(
          "relative",
          folded &&
            "max-h-48 overflow-hidden after:absolute after:inset-x-0 after:bottom-0 after:h-12 after:bg-gradient-to-t after:from-background after:to-transparent"
        )}
      >
        {children}
      </div>
      {long && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => {
            setOpen((o) => !o);
          }}
          className="mt-2 min-h-11 text-sm font-medium text-primary hover:underline"
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
